"use strict";
// (C) 2019-2022 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPivotTableConfig = exports.PluggablePivotTable = exports.getRowAttributes = exports.getColumnAttributes = void 0;
var tslib_1 = require("tslib");
var drillDownUtil_1 = require("../drillDownUtil");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var isNil_1 = tslib_1.__importDefault(require("lodash/isNil"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_pivot_1 = require("@gooddata/sdk-ui-pivot");
var react_1 = tslib_1.__importDefault(require("react"));
var react_dom_1 = require("react-dom");
var react_measure_1 = tslib_1.__importDefault(require("react-measure"));
var bucket_1 = require("../../../constants/bucket");
var properties_1 = require("../../../constants/properties");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var dimensions_1 = require("../../../utils/dimensions");
var domHelper_1 = require("../../../utils/domHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var pivotTableUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/pivotTableUiConfigHelper");
var UnsupportedConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/UnsupportedConfigurationPanel"));
var AbstractPluggableVisualization_1 = require("../AbstractPluggableVisualization");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var widthItemsHelpers_1 = require("./widthItemsHelpers");
var sortItemsHelpers_1 = require("./sortItemsHelpers");
var totalsHelpers_1 = require("./totalsHelpers");
exports.getColumnAttributes = function (buckets) {
    return bucketHelper_1.getItemsFromBuckets(buckets, [sdk_ui_1.BucketNames.COLUMNS, sdk_ui_1.BucketNames.STACK, sdk_ui_1.BucketNames.SEGMENT], [bucket_1.ATTRIBUTE, bucket_1.DATE]);
};
exports.getRowAttributes = function (buckets) {
    return bucketHelper_1.getItemsFromBuckets(buckets, [
        sdk_ui_1.BucketNames.ATTRIBUTE,
        sdk_ui_1.BucketNames.ATTRIBUTES,
        sdk_ui_1.BucketNames.VIEW,
        sdk_ui_1.BucketNames.TREND,
        sdk_ui_1.BucketNames.LOCATION,
    ], [bucket_1.ATTRIBUTE, bucket_1.DATE]);
};
var PluggablePivotTable = /** @class */ (function (_super) {
    tslib_1.__extends(PluggablePivotTable, _super);
    function PluggablePivotTable(props) {
        var _a;
        var _this = _super.call(this, props) || this;
        _this.createCorePivotTableProps = function () {
            var onColumnResized = isManualResizingEnabled(_this.settings) ? _this.onColumnResized : undefined;
            return {
                intl: _this.intl,
                ErrorComponent: null,
                LoadingComponent: null,
                onDrill: _this.onDrill,
                afterRender: _this.afterRender,
                onLoadingChanged: _this.onLoadingChanged,
                pushData: _this.handlePushData,
                onError: _this.onError,
                onExportReady: _this.onExportReady,
                onColumnResized: onColumnResized,
            };
        };
        _this.environment = props.environment;
        _this.renderFun = props.renderFun;
        _this.settings = (_a = props.featureFlags) !== null && _a !== void 0 ? _a : {};
        _this.onColumnResized = _this.onColumnResized.bind(_this);
        _this.handlePushData = _this.handlePushData.bind(_this);
        _this.supportedPropertiesList = supportedProperties_1.PIVOT_TABLE_SUPPORTED_PROPERTIES;
        _this.backendCapabilities = props.backend.capabilities;
        return _this;
    }
    PluggablePivotTable.prototype.unmount = function () {
        domHelper_1.unmountComponentsAtNodes([this.element, this.configPanelElement]);
    };
    PluggablePivotTable.prototype.getExtendedReferencePoint = function (referencePoint, previousReferencePoint) {
        var _a, _b, _c, _d, _e;
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: pivotTableUiConfigHelper_1.getPivotTableDefaultUiConfig(multipleDatesEnabled(this.settings)) });
        var buckets = newReferencePoint.buckets;
        var measures = bucketHelper_1.getAllItemsByType(buckets, [bucket_1.METRIC]);
        var rowAttributes = exports.getRowAttributes(buckets);
        var previousRowAttributes = previousReferencePoint && exports.getRowAttributes(previousReferencePoint.buckets);
        var columnAttributes = exports.getColumnAttributes(buckets);
        var previousColumnAttributes = previousReferencePoint && exports.getColumnAttributes(previousReferencePoint.buckets);
        var filters = newReferencePoint.filters
            ? flatMap_1.default(newReferencePoint.filters.items, function (item) { return item.filters; })
            : [];
        var totals = totalsHelpers_1.removeInvalidTotals(bucketHelper_1.getTotalsFromBucket(buckets, sdk_ui_1.BucketNames.ATTRIBUTE), filters);
        newReferencePoint.buckets = bucketHelper_1.removeDuplicateBucketItems([
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: measures,
            },
            tslib_1.__assign({ localIdentifier: sdk_ui_1.BucketNames.ATTRIBUTE, items: rowAttributes }, (totals.length > 0 ? { totals: totals } : null)),
            {
                localIdentifier: sdk_ui_1.BucketNames.COLUMNS,
                items: columnAttributes,
            },
        ]);
        var originalSortItems = (_b = (_a = newReferencePoint.properties) === null || _a === void 0 ? void 0 : _a.sortItems) !== null && _b !== void 0 ? _b : [];
        var originalColumnWidths = (_d = (_c = newReferencePoint.properties) === null || _c === void 0 ? void 0 : _c.controls) === null || _d === void 0 ? void 0 : _d.columnWidths;
        var columnWidths = widthItemsHelpers_1.adaptReferencePointWidthItemsToPivotTable(originalColumnWidths, measures, rowAttributes, columnAttributes, previousRowAttributes ? previousRowAttributes : [], previousColumnAttributes ? previousColumnAttributes : [], filters);
        var controlsObj = columnWidths
            ? {
                controls: {
                    columnWidths: columnWidths,
                },
            }
            : {};
        newReferencePoint.properties = tslib_1.__assign({ sortItems: sortItemsHelpers_1.addDefaultSort(sortItemsHelpers_1.adaptReferencePointSortItemsToPivotTable(originalSortItems, measures, rowAttributes, columnAttributes), filters, rowAttributes, previousRowAttributes, columnAttributes, tableSortingCheckDisabled(this.settings)) }, controlsObj);
        pivotTableUiConfigHelper_1.setPivotTableUiConfig(newReferencePoint, this.intl, sdk_ui_1.VisualizationTypes.TABLE);
        bucketConfig_1.configurePercent(newReferencePoint, false);
        bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!((_e = this.settings) === null || _e === void 0 ? void 0 : _e["enableWeekFilters"]));
        Object.assign(newReferencePoint, propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList));
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggablePivotTable.prototype.getInsightWithDrillDownApplied = function (sourceVisualization, drillDownContext) {
        var drillDownInsight = drillDownUtil_1.modifyBucketsAttributesForDrillDown(sourceVisualization, drillDownContext.drillDefinition);
        var drillDownInsightWithFilters = drillDownUtil_1.addIntersectionFiltersToInsight(drillDownInsight, drillDownContext.event.drillContext.intersection);
        return drillDownUtil_1.sanitizeTableProperties(sdk_model_1.insightSanitize(drillDownInsightWithFilters));
    };
    PluggablePivotTable.prototype.getExecution = function (options, insight, executionFactory) {
        var _a, _b;
        var dateFormat = options.dateFormat, executionConfig = options.executionConfig;
        return (_a = (_b = executionFactory
            .forInsight(insight))
            .withDimensions.apply(_b, this.getDimensions(insight)))
            .withSorting.apply(_a, getPivotTableSortItems(insight)).withDateFormat(dateFormat)
            .withExecConfig(executionConfig);
    };
    PluggablePivotTable.prototype.updateInstanceProperties = function (options, insight, 
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    insightPropertiesMeta) {
        _super.prototype.updateInstanceProperties.call(this, options, insight, insightPropertiesMeta);
        /*
         * This was ported from v7. For some reason (likely related KD interop?) the entire content of properties
         * would be picked up and used.
         */
        this.visualizationProperties = sdk_model_1.insightProperties(insight);
        this.adaptPropertiesToInsight(this.visualizationProperties, this.currentInsight);
    };
    PluggablePivotTable.prototype.renderVisualization = function (options, insight, executionFactory) {
        if (!sdk_model_1.insightHasDataDefined(insight)) {
            // there is nothing in the insight's bucket that can be visualized
            // bail out
            return;
        }
        var locale = options.locale, custom = options.custom, dimensions = options.dimensions, _a = options.config, config = _a === void 0 ? {} : _a, _b = options.customVisualizationConfig, customVisualizationConfig = _b === void 0 ? {} : _b, theme = options.theme;
        var maxHeight = config.maxHeight, maxWidth = config.maxWidth;
        var height = dimensions === null || dimensions === void 0 ? void 0 : dimensions.height;
        var drillableItems = custom.drillableItems;
        var execution = this.getExecution(options, insight, executionFactory);
        var columnWidths = propertiesHelper_1.getColumnWidthsFromProperties(sdk_model_1.insightProperties(insight));
        var tableConfig = tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, createPivotTableConfig(config, this.environment, this.settings, this.backendCapabilities, columnWidths)), customVisualizationConfig), { maxHeight: maxHeight,
            maxWidth: maxWidth });
        var pivotTableProps = tslib_1.__assign(tslib_1.__assign({}, this.createCorePivotTableProps()), { execution: execution,
            drillableItems: drillableItems, config: tableConfig, locale: locale,
            theme: theme });
        if (this.environment === properties_1.DASHBOARDS_ENVIRONMENT) {
            this.renderFun(react_1.default.createElement(react_measure_1.default, { client: true }, function (_a) {
                var measureRef = _a.measureRef, contentRect = _a.contentRect;
                var clientHeight = contentRect.client.height;
                var pivotWrapperStyle = {
                    height: isNil_1.default(height) ? "100%" : height,
                    textAlign: "left",
                    display: "flex",
                    flex: "1 1 auto",
                    flexDirection: "column",
                };
                var configWithMaxHeight = tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, tableConfig), { maxHeight: clientHeight }), customVisualizationConfig);
                return (react_1.default.createElement("div", { ref: measureRef, style: pivotWrapperStyle, className: "gd-table-dashboard-wrapper" },
                    react_1.default.createElement(sdk_ui_pivot_1.CorePivotTable, tslib_1.__assign({}, pivotTableProps, { config: configWithMaxHeight }))));
            }), document.querySelector(this.element));
        }
        else {
            this.renderFun(react_1.default.createElement(sdk_ui_pivot_1.CorePivotTable, tslib_1.__assign({}, pivotTableProps)), document.querySelector(this.element));
        }
    };
    PluggablePivotTable.prototype.renderConfigurationPanel = function (insight) {
        var _a;
        if (document.querySelector(this.configPanelElement)) {
            var properties = (_a = this.visualizationProperties) !== null && _a !== void 0 ? _a : {};
            // we need to handle cases when attribute previously bearing the default sort is no longer available
            var sanitizedProperties = properties.sortItems
                ? tslib_1.__assign(tslib_1.__assign({}, properties), { sortItems: sortItemsHelpers_1.sanitizePivotTableSorts(properties.sortItems, sdk_model_1.insightBuckets(insight)) }) : properties;
            react_dom_1.render(react_1.default.createElement(UnsupportedConfigurationPanel_1.default, { locale: this.locale, pushData: this.pushData, properties: sanitizedProperties }), document.querySelector(this.configPanelElement));
        }
    };
    PluggablePivotTable.prototype.getDimensions = function (insight) {
        return dimensions_1.generateDimensions(insight, sdk_ui_1.VisualizationTypes.TABLE);
    };
    PluggablePivotTable.prototype.adaptPropertiesToInsight = function (visualizationProperties, insight) {
        // This is sanitization of properties from KD vs current mdObject from AD
        var columnWidths = propertiesHelper_1.getColumnWidthsFromProperties(visualizationProperties);
        if (columnWidths) {
            this.sanitizeColumnWidths(columnWidths, insight);
        }
    };
    PluggablePivotTable.prototype.sanitizeColumnWidths = function (columnWidths, insight) {
        if (isEmpty_1.default(sdk_model_1.insightBuckets(insight))) {
            return;
        }
        var adaptedColumnWidths = widthItemsHelpers_1.adaptMdObjectWidthItemsToPivotTable(columnWidths, insight);
        if (!isEqual_1.default(columnWidths, adaptedColumnWidths)) {
            this.visualizationProperties.controls.columnWidths = adaptedColumnWidths;
            this.pushData({
                properties: {
                    controls: {
                        columnWidths: adaptedColumnWidths,
                    },
                },
            });
        }
    };
    PluggablePivotTable.prototype.onColumnResized = function (columnWidths) {
        this.pushData({
            properties: {
                controls: {
                    columnWidths: columnWidths,
                },
            },
        });
    };
    PluggablePivotTable.prototype.handlePushData = function (data) {
        if (data && data.properties && data.properties.sortItems) {
            this.pushData({
                properties: {
                    sortItems: data.properties.sortItems,
                },
            });
        }
        else {
            this.pushData(data);
        }
    };
    return PluggablePivotTable;
}(AbstractPluggableVisualization_1.AbstractPluggableVisualization));
exports.PluggablePivotTable = PluggablePivotTable;
function isManualResizingEnabled(settings) {
    return settings["enableTableColumnsManualResizing"] === true;
}
function multipleDatesEnabled(settings) {
    return settings["enableMultipleDates"] === true;
}
function tableSortingCheckDisabled(settings) {
    return settings["tableSortingCheckDisabled"] === true;
}
/**
 * Given plug viz GDC config, current environment and platform settings, this creates pivot table config.
 *
 * @internal
 */
function createPivotTableConfig(config, environment, settings, capabilities, columnWidths) {
    var tableConfig = {
        separators: config.separators,
    };
    if (environment !== properties_1.DASHBOARDS_ENVIRONMENT) {
        tableConfig = tslib_1.__assign(tslib_1.__assign({}, tableConfig), { menu: sdk_ui_pivot_1.pivotTableMenuForCapabilities(capabilities, {
                aggregations: true,
                aggregationsSubMenu: true,
            }) });
    }
    var autoSize = settings["enableTableColumnsAutoResizing"];
    // the growToFit can only be enabled in dashboards
    var growToFit = environment === properties_1.DASHBOARDS_ENVIRONMENT && settings["enableTableColumnsGrowToFit"];
    var manualResizing = settings["enableTableColumnsManualResizing"];
    var columnSizing = {};
    if (autoSize) {
        columnSizing = {
            defaultWidth: config.isExportMode ? "viewport" : "autoresizeAll",
        };
    }
    if (growToFit) {
        columnSizing = tslib_1.__assign(tslib_1.__assign({}, columnSizing), { growToFit: true });
    }
    if (manualResizing && columnWidths && columnWidths.length > 0) {
        columnSizing = tslib_1.__assign(tslib_1.__assign({}, columnSizing), { columnWidths: columnWidths });
    }
    if (environment === properties_1.ANALYTICAL_ENVIRONMENT) {
        columnSizing = tslib_1.__assign(tslib_1.__assign({}, columnSizing), { growToFit: false });
    }
    return tslib_1.__assign(tslib_1.__assign({}, tableConfig), { columnSizing: columnSizing });
}
exports.createPivotTableConfig = createPivotTableConfig;
/**
 * This function exists to overcome AD weirdness where AD will sometimes send insight without any
 * sorts even if the pivot table should be sorted by default by the first row attribute in ascending order. Code here
 * fixes this symptom and ensures the default sort is in place.
 *
 * Note: while this may seem small thing, it's actually a messy business. When rendering / switching to the pivot
 * table the AD will call update/render multiple times. Sometimes with sort items, sometimes without sort items. This
 * can seriously mess up the pivot table in return: the column resizing is susceptible to race conditions and timing
 * issues. Because of the flurry of calls, the table may not render or may render not resized at all.
 */
function getPivotTableSortItems(insight) {
    var sorts = sdk_model_1.insightSorts(insight);
    if (!isEmpty_1.default(sorts)) {
        /*
         * This is here to ensure that when rendering pivot table in KD, all invalid sort items
         * are filtered out. At this moment, core pivot table does not handle invalid sorts so well and
         * they can knock it off balance and it won't show up (interplay with resizing).
         *
         * Fixing core pivot to strip out invalid sorts has to happen one day - however regardless of that,
         * it is still the responsibility of the PluggablePivotTable to call the CorePivot correctly and so this
         * sanitization here also makes sense.
         */
        return sortItemsHelpers_1.sanitizePivotTableSorts(sorts, sdk_model_1.insightBuckets(insight));
    }
    var rowBucket = sdk_model_1.insightBucket(insight, sdk_ui_1.BucketNames.ATTRIBUTE);
    var rowAttribute = rowBucket && sdk_model_1.bucketAttribute(rowBucket);
    if (rowAttribute) {
        return [sdk_model_1.newAttributeSort(rowAttribute, "asc")];
    }
}
//# sourceMappingURL=PluggablePivotTable.js.map